/**
 * One payment, in full — and, once it is confirmed, the receipt for it.
 *
 * Deliberately a different document from an invoice, because the two records
 * are different things: an invoice is what was ASKED for and can be corrected;
 * a payment is money that MOVED and never gets rewritten. This page shows what
 * moved, through which gateway, what period it bought, and which invoices it
 * cleared — the last of those read from `invoices.payment_id`, which
 * settlement writes, rather than inferred from dates lining up.
 */
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, History, Paperclip } from 'lucide-react';
import { invoiceNumber, receiptNumber, type PaymentDetail } from '@simpleacs/shared';
import { Card, ErrorNotice, Pill, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { INVOICE_TONE, METHOD_LABEL, monthDate, periodLabel, STATUS_TONE } from './common';
import {
  BackLink,
  BrandHeader,
  DocumentActions,
  HistoryList,
  LineTable,
  MetaGrid,
  Parties,
} from './parts';

export function PaymentDetailPage() {
  // The payment's NUMBER — `/billing/payments/42`. Its opaque id still resolves
  // server-side, so a bookmark from before the numbers existed still opens.
  const { ref = '' } = useParams();
  const query = useQuery({
    queryKey: ['billing', 'payment', ref],
    queryFn: () => api.get<PaymentDetail>(`/billing/payments/${encodeURIComponent(ref)}`),
  });

  if (query.isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (query.error) return <ErrorNotice error={query.error} />;
  const payment = query.data;
  if (!payment) return null;

  // Only confirmed money is a receipt. Calling a pending attempt one would be
  // a document a payer could wave at the operator.
  const isReceipt = payment.status === 'confirmed';

  return (
    <>
      <BackLink to="/billing">Back to billing</BackLink>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            {/* The NUMBER leads, exactly as the invoice page does — a document
                is titled by what you look it up under. The amount moves one
                line down and loses nothing: it is still the first thing in the
                subtitle, and it is the largest figure on the document sheet
                below. */}
            <h1 className="font-mono text-xl font-semibold text-ink">
              {receiptNumber(payment.number)}
            </h1>
            <Pill state tone={STATUS_TONE[payment.status]}>{payment.status}</Pill>
            <span className="text-sm text-muted">{METHOD_LABEL[payment.method]}</span>
          </div>
          <p className="mt-1 text-sm text-muted">
            {formatMoney(payment.amountMinor, payment.currency)} ·{' '}
            {payment.orgName ?? 'Unknown organization'} · started {monthDate(payment.createdAt)}
          </p>
        </div>
        <DocumentActions pdfHref={`/api/billing/payments/${encodeURIComponent(payment.id)}/pdf`} />
      </div>

      <div className="space-y-5">
        <section className="doc-sheet card overflow-hidden">
          <BrandHeader
            brand={payment.brand}
            title={isReceipt ? 'RECEIPT' : 'PAYMENT'}
            reference={receiptNumber(payment.number)}
          />
          <Parties
            brand={payment.brand}
            toTitle="Received from"
            toName={payment.orgName ?? 'Unknown organization'}
            toSub={payment.orgSlug}
          />
          <MetaGrid
            items={[
              {
                label: isReceipt ? 'Receipt number' : 'Payment number',
                value: (
                  <span className="font-mono text-xs">{receiptNumber(payment.number)}</span>
                ),
              },
              {
                // The GATEWAY's own reference — an M-PESA code, a checkout
                // session. It used to fall back to the payment id when there
                // was none; the document now has a number of its own to be
                // identified by, and a bank transfer approved by hand simply
                // has no gateway code to print.
                label: 'Gateway reference',
                value: <span className="font-mono text-xs">{payment.reference ?? '—'}</span>,
              },
              { label: 'Method', value: METHOD_LABEL[payment.method] },
              { label: 'Status', value: payment.status },
              { label: 'Started', value: monthDate(payment.createdAt) },
              {
                label: isReceipt ? 'Confirmed' : 'Settled',
                value: payment.confirmedAt ? monthDate(payment.confirmedAt) : '—',
              },
              { label: 'Covers', value: periodLabel(payment.periodStart, payment.periodEnd) },
            ]}
          />
          <LineTable
            lines={payment.lines}
            currency={payment.currency}
            totalMinor={payment.amountMinor}
            fallbackLabel={`Platform service — ${payment.months} month${payment.months === 1 ? '' : 's'}`}
            accentColor={payment.brand.accentColor}
          />
          {payment.note && (
            <div className="doc-keep border-t border-line px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-faint">Note</p>
              <p className="mt-1.5 whitespace-pre-wrap text-sm text-muted">{payment.note}</p>
            </div>
          )}
          {payment.brand.footerNote && (
            <p className="border-t border-line px-5 py-2.5 text-center text-xs text-faint">
              {payment.brand.footerNote}
            </p>
          )}
        </section>

        {payment.hasProof && (
          <Card
            title={
              <span className="flex items-center gap-2">
                <Paperclip className="size-4 text-muted" />
                Bank proof
              </span>
            }
          >
            <p className="px-5 py-3.5 text-sm text-muted">
              The payer uploaded a slip with this transfer.{' '}
              <a
                className="text-primary hover:underline"
                href={`/api/billing/payments/${encodeURIComponent(payment.id)}/proof`}
                target="_blank"
                rel="noreferrer"
              >
                View proof
              </a>
            </p>
          </Card>
        )}

        <Card
          title={
            <span className="flex items-center gap-2">
              <FileText className="size-4 text-muted" />
              Invoices settled
            </span>
          }
        >
          {payment.invoices.length === 0 ? (
            <p className="px-5 py-4 text-sm text-muted">
              This payment cleared no invoice. Not an error: a payment never creates one, and an
              org can pay ahead of the month being invoiced.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {payment.invoices.map((invoice) => (
                <li key={invoice.id}>
                  <Link
                    to={`/billing/invoices/${invoice.number}`}
                    className="doc-keep flex flex-wrap items-center justify-between gap-3 px-5 py-3 hover:bg-surface-2/70"
                  >
                    <span className="flex flex-wrap items-center gap-3 text-sm text-muted">
                      <b className="font-mono text-ink">{invoiceNumber(invoice.number)}</b>
                      <Pill state tone={INVOICE_TONE[invoice.status] ?? 'neutral'}>{invoice.status}</Pill>
                      {formatMoney(invoice.amountMinor, invoice.currency)}
                      <span>{periodLabel(invoice.periodStart, invoice.periodEnd)}</span>
                    </span>
                    <span className="print:hidden text-xs text-primary">Open the invoice</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title={
            <span className="flex items-center gap-2">
              <History className="size-4 text-muted" />
              History
            </span>
          }
        >
          <HistoryList history={payment.history} />
        </Card>
      </div>
    </>
  );
}
